import { c as _c } from "react/compiler-runtime";
/**
 * Хук для управления виджетом Intercom
 * Отвечает за:
 * - Определение видимости виджета на основе покупок пользователя и текущего роута
 * - Отслеживание непрочитанных сообщений
 * - Инициализацию и конфигурацию Intercom
 */

import Intercom, { onUnreadCountChange } from '@intercom/messenger-js-sdk';
import { useEffect, useMemo, useState } from 'react';
import type { User } from '@/entities/User/types/user';
interface UseIntercomManagerParams {
  /** Данные текущего пользователя */
  user: User | null;
  /** JWT токен для аутентификации в Intercom */
  jwtData?: {
    intercomJwt: string;
  } | null;
  /** Флаг загрузки JWT токена */
  isLoading: boolean;
  /** Текущий путь роута */
  pathname: string | null;
}
interface UseIntercomManagerReturn {
  /** Должен ли виджет Intercom быть видимым */
  shouldShowIntercom: boolean;
  /** Количество непрочитанных сообщений */
  unreadCount: number;
}

/**
 * Хук для управления виджетом Intercom
 *
 * @example
 * ```tsx
 * const { shouldShowIntercom, unreadCount } = useIntercomManager({
 *   user,
 *   jwtData,
 *   isLoading,
 *   pathname,
 * });
 * ```
 */
export function useIntercomManager(t0) {
  const $ = _c(25);
  const {
    user,
    jwtData,
    isLoading,
    pathname
  } = t0;
  const [unreadCount, setUnreadCount] = useState(0);
  user?.totalOwnedEe;
  let t1;
  bb0: {
    const hasActivePurchases = (user?.totalOwnedEe ?? 0) > 0;
    if (!hasActivePurchases) {
      t1 = false;
      break bb0;
    }
    let t2;
    if ($[0] !== pathname) {
      t2 = pathname?.includes("/settings") ?? false;
      $[0] = pathname;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    const isInSettings = t2;
    const hasUnreadMessages = unreadCount > 0;
    t1 = isInSettings || hasUnreadMessages;
  }
  const shouldShowIntercom = t1;
  let t2;
  if ($[2] !== user?.id || $[3] !== user?.showIntercom) {
    t2 = () => {
      if (user?.showIntercom && user?.id) {
        onUnreadCountChange(count => {
          setUnreadCount(count);
        });
      }
    };
    $[2] = user?.id;
    $[3] = user?.showIntercom;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = user?.showIntercom;
  const t4 = user?.id;
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = [t3, t4];
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  useEffect(t2, t5);
  let t6;
  if ($[8] !== isLoading || $[9] !== jwtData || $[10] !== shouldShowIntercom || $[11] !== user) {
    t6 = () => {
      if (typeof window !== "undefined") {
        (window as typeof window & {
          intercomSettings?: Record<string, unknown>;
        }).intercomSettings = {
          app_id: "iy4klso6",
          alignment: "right",
          vertical_padding: 100,
          horizontal_padding: 20
        };
      }
      if (user?.showIntercom && user?.id && jwtData?.intercomJwt && !isLoading) {
        Intercom({
          app_id: "iy4klso6",
          action_color: "#0091FF",
          user_id: user.id.toString(),
          name: user.username,
          email: user.email,
          created_at: parseInt((new Date(user.createdAt as string).getTime() / 1000).toFixed(0), 10),
          intercom_user_jwt: jwtData.intercomJwt,
          hide_default_launcher: !shouldShowIntercom
        });
      } else {
        Intercom({
          app_id: "iy4klso6",
          hide_default_launcher: true
        });
      }
    };
    $[8] = isLoading;
    $[9] = jwtData;
    $[10] = shouldShowIntercom;
    $[11] = user;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = user?.showIntercom;
  const t8 = user?.id;
  const t9 = user?.username;
  const t10 = user?.email;
  const t11 = user?.createdAt;
  const t12 = jwtData?.intercomJwt;
  let t13;
  if ($[13] !== isLoading || $[14] !== shouldShowIntercom || $[15] !== t10 || $[16] !== t11 || $[17] !== t12 || $[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t13 = [t7, t8, t9, t10, t11, t12, isLoading, shouldShowIntercom];
    $[13] = isLoading;
    $[14] = shouldShowIntercom;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  useEffect(t6, t13);
  let t14;
  if ($[22] !== shouldShowIntercom || $[23] !== unreadCount) {
    t14 = {
      shouldShowIntercom,
      unreadCount
    };
    $[22] = shouldShowIntercom;
    $[23] = unreadCount;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  return t14;
}